import { useQuery } from "@tanstack/react-query";
import type { SearchQuery, SearchResponse, SearchResult } from "algoliasearch/lite";

import type { AlgoliaSearchResult } from "@/models/AlgoliaSearchResult";
import {
  ALGOLIA_PRODUCT_TYPES,
  buildAnalyticsTags,
  buildCatalogFilters,
  CATALOG_CARD_ATTRIBUTES,
  type CatalogHit,
  type CatalogVisibility,
  getBestSellingIndexName,
  getPrimarySearchIndexName,
  isCatalogHit,
} from "@/utils/algoliaCatalog";
import { getAlgoliaSearchClient } from "@/utils/algoliaSearchClient";
import { getListings, getPlatform } from "api/methods";
import { STALE_TIME } from "api/staleTimes";
import type { Models } from "api/types";
import { useCatalogLocationId } from "hooks/useCatalogLocationId";
import { useMyProductsVisibility } from "hooks/useMyProductsVisibility";
import { useSkinDropVisibility } from "hooks/useSkinDropVisibility";
import { useUser } from "modules/user/useUser/useUser";

/** How many cards the best-selling rails show. */
export const RAIL_HITS_PER_PAGE = 5;
const RETAIL_RAIL_HITS_PER_PAGE = 4;
/**
 * Fetched per rail before the cards a rail cannot render (no image, title or price) are dropped, so
 * the rail still fills its five slots when the top sellers include a few bare records.
 */
export const RAIL_FETCH_HITS = 50;

export type CatalogSummary = {
  /** Top-selling treatments and packages, from the `_best_selling` replica. */
  bestSelling: CatalogHit[];
  /** Top-selling retail products the practice shows; empty when retail is off. */
  retail: CatalogHit[];
  /** Visible records per `productType` facet value (`ALGOLIA_PRODUCT_TYPES`). */
  productTypeCounts: Record<string, number>;
  /** Visible treatments and packages per concern id — what "browse by concern" has to offer. */
  concernCounts: Record<string, number>;
  hasInStoreProducts: boolean;
  hasSkinDropProducts: boolean;
  /** Every visible record for the practice at this location. */
  totalCount: number;
};

export type CatalogSummaryInput = {
  platformId: string;
  platformLocationId: string | null | undefined;
  visibility: CatalogVisibility;
};

export type CatalogListingType = Extract<Models<"ListingModel">["type"], "Treatment" | "Package">;

const productTypeForListingType = (type: CatalogListingType) =>
  type === "Treatment" ? ALGOLIA_PRODUCT_TYPES.treatment : ALGOLIA_PRODUCT_TYPES.package;

const catalogCountForListingType = (catalog: CatalogSummary, type: CatalogListingType) => {
  const count = catalog.productTypeCounts[productTypeForListingType(type)];
  return typeof count === "number" ? count : 0;
};

/** Whether one specific indexed category needs primary-catalog corroboration. */
export const needsCatalogListingVerification = (
  catalog: CatalogSummary,
  type: CatalogListingType,
): boolean => catalogCountForListingType(catalog, type) === 0;

/** A missing facet is verified against the primary catalog before it can hide real listings. */
export const needsCatalogListingsVerification = (catalog: CatalogSummary): boolean =>
  needsCatalogListingVerification(catalog, "Treatment") ||
  needsCatalogListingVerification(catalog, "Package");

/** Whether a page must wait for primary-catalog corroboration before settling its category UI. */
export const shouldVerifyCatalogListings = (
  catalog: CatalogSummary | undefined,
  enabled: boolean,
): boolean => enabled && catalog !== undefined && needsCatalogListingsVerification(catalog);

/** Landing content waits only while required corroboration is pending; failure stays non-fatal. */
export const shouldWaitForCatalogListingsVerification = (
  catalog: CatalogSummary | undefined,
  enabled: boolean,
  isFetched: boolean,
  isError: boolean,
): boolean => shouldVerifyCatalogListings(catalog, enabled) && !isFetched && !isError;

/**
 * REST listings are rendered only for a type the indexed summary says is empty. A healthy index
 * remains the source of ordering and filtering for its non-empty types.
 */
export const catalogUsesRestListings = (
  catalog: CatalogSummary,
  listings: readonly Models<"ListingModel">[] | undefined,
  type: CatalogListingType,
): Models<"ListingModel">[] => {
  if (!needsCatalogListingVerification(catalog, type) || listings === undefined) return [];
  return listings.filter((listing) => listing.type === type);
};

export type CatalogListingsVerificationInput = {
  platformId: string;
  platformLocationId: string | null | undefined;
};

/**
 * The endpoint infers the current patient location, while the cache key names that location
 * explicitly so switching locations cannot reuse a completed verification from the prior shop.
 */
export const catalogListingsVerificationQuery = (input: CatalogListingsVerificationInput) => ({
  queryKey: [
    "shop/catalog-listings-verification",
    input.platformId,
    input.platformLocationId ?? null,
  ] as const,
  queryFn: () => getListings(),
  staleTime: STALE_TIME.catalog,
});

const isHitsResponse = <T>(result: SearchResult<T>): result is SearchResponse<T> =>
  "hits" in result;

const countTrue = (facet: Record<string, number> | undefined) => (facet?.true ?? 0) > 0;

/** A rail card needs an image, a title and a price; a record missing any is skipped, not shown bare. */
const canRenderRailCard = (hit: CatalogHit) =>
  !!hit.imageUrl && !!hit.title && typeof hit.minBasePrice === "number" && hit.minBasePrice > 0;

const railCards = (hits: readonly AlgoliaSearchResult[], count: number): CatalogHit[] =>
  hits.filter(isCatalogHit).filter(canRenderRailCard).slice(0, count);

/**
 * One multi-query request that replaces `GET /listings` on `/shop` and `/home`: the two rails come
 * from the best-selling replica and every "is there anything to show" boolean the pages used to
 * derive by scanning the catalog comes from facet counts on the primary index.
 */
export const fetchCatalogSummary = async ({
  platformId,
  platformLocationId,
  visibility,
}: CatalogSummaryInput): Promise<CatalogSummary> => {
  const analyticsTags = buildAnalyticsTags(platformId, "rail");
  const baseFilter = { platformId, platformLocationId, visibility };
  const includeRetail = !!(visibility.areRetailProductsVisible || visibility.isSkinDropVisible);

  const requests: SearchQuery[] = [
    {
      indexName: getBestSellingIndexName(),
      query: "",
      hitsPerPage: RAIL_FETCH_HITS,
      attributesToRetrieve: CATALOG_CARD_ATTRIBUTES,
      attributesToHighlight: [],
      filters: buildCatalogFilters({
        ...baseFilter,
        productTypes: [ALGOLIA_PRODUCT_TYPES.treatment, ALGOLIA_PRODUCT_TYPES.package],
      }),
      // The concern pages list treatments and packages only, so concerns are counted over this rail's filter.
      facets: ["concernIds"],
      analytics: false,
      analyticsTags,
    },
    {
      indexName: getPrimarySearchIndexName(),
      query: "",
      hitsPerPage: 0,
      filters: buildCatalogFilters(baseFilter),
      facets: ["productType", "requires.skinDrop", "requires.myProducts"],
      // A count lookup, not a search a patient ran: keep it out of the search analytics.
      analytics: false,
      analyticsTags,
    },
  ];

  if (includeRetail) {
    requests.push({
      indexName: getBestSellingIndexName(),
      query: "",
      hitsPerPage: RAIL_FETCH_HITS,
      attributesToRetrieve: CATALOG_CARD_ATTRIBUTES,
      attributesToHighlight: [],
      filters: buildCatalogFilters({
        ...baseFilter,
        productTypes: [ALGOLIA_PRODUCT_TYPES.retailProduct],
      }),
      analytics: false,
      analyticsTags,
    });
  }

  const { results } = await getAlgoliaSearchClient().search<AlgoliaSearchResult>({ requests });
  const [rail, summary, retail] = results;

  if (!rail || !summary || !isHitsResponse(rail) || !isHitsResponse(summary)) {
    throw new Error("Algolia catalog summary returned an unexpected response shape.");
  }

  const facets = summary.facets ?? {};

  return {
    bestSelling: railCards(rail.hits, RAIL_HITS_PER_PAGE),
    retail:
      retail && isHitsResponse(retail) ? railCards(retail.hits, RETAIL_RAIL_HITS_PER_PAGE) : [],
    productTypeCounts: facets.productType ?? {},
    concernCounts: rail.facets?.concernIds ?? {},
    hasInStoreProducts: countTrue(facets["requires.myProducts"]),
    hasSkinDropProducts: countTrue(facets["requires.skinDrop"]),
    totalCount: summary.nbHits ?? 0,
  };
};

export const catalogSummaryQuery = (input: CatalogSummaryInput) => ({
  queryKey: [
    "algolia/catalog-summary",
    input.platformId,
    input.platformLocationId ?? null,
    !!input.visibility.areRetailProductsVisible,
    !!input.visibility.isSkinDropVisible,
  ],
  queryFn: () => fetchCatalogSummary(input),
  staleTime: 1000 * 60,
});

/**
 * The catalog summary for the signed-in (or anonymous) patient at their current location, once the
 * platform, user and retail-visibility answers it depends on are in. Every consumer shares the one
 * cache entry.
 */
export const useCatalogSummary = () => {
  const { data: platform } = useQuery({ ...getPlatform.keyed() });
  const { user } = useUser();
  const { isSkinDropVisibleOnMarketplace, isSkinDropLoading } = useSkinDropVisibility();
  const { isMyProductsVisibleOnMarketplace, isMyProductsLoading } = useMyProductsVisibility();

  const {
    platformLocationId,
    isReady: isLocationReady,
    error: locationError,
  } = useCatalogLocationId(user);

  const platformId = platform?.id;

  const summary = useQuery({
    ...catalogSummaryQuery({
      platformId: platformId ?? "",
      platformLocationId,
      visibility: {
        areRetailProductsVisible: isMyProductsVisibleOnMarketplace,
        isSkinDropVisible: isSkinDropVisibleOnMarketplace,
      },
    }),
    enabled:
      !!platformId && !!user && isLocationReady && !isSkinDropLoading && !isMyProductsLoading,
  });
  // A visitor whose location lookup failed never gets a summary query, so that failure is the summary's error.
  return locationError ? { ...summary, error: locationError } : summary;
};

/** Shared, location-scoped verification used by the shop navigation and landing page. */
export const useCatalogListingsVerification = ({
  catalog,
  enabled,
}: {
  catalog: CatalogSummary | undefined;
  enabled: boolean;
}) => {
  const { data: platform } = useQuery({ ...getPlatform.keyed() });
  const { user } = useUser();
  const {
    platformLocationId,
    isReady: isLocationReady,
    error: locationError,
  } = useCatalogLocationId(user);
  const shouldVerify = shouldVerifyCatalogListings(catalog, enabled);

  const query = useQuery({
    ...catalogListingsVerificationQuery({
      platformId: platform?.id ?? "",
      platformLocationId,
    }),
    enabled:
      shouldVerify &&
      platform !== undefined &&
      user !== undefined &&
      isLocationReady &&
      locationError === null,
  });

  return query;
};
